import React, { useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import FlightBookingSidebar from "../FlightBookingSidebar";
import { SelfComposeUiProvider, useSelfComposeUi } from "../selfCompose/SelfComposeUiContext";
import { useSearch } from "../../../../contexts/SearchContext";
import { useBookingFlow } from "../hooks/useBookingFlow";
import FlightWizardNavigation from "./FlightWizardNavigation";
import FlightWizardStepHeader from "./FlightWizardStepHeader";
import FlightWizardStepStrip from "./FlightWizardStepStrip";
import { FlightResultsPageLayout, FlightSearchPageLayout } from "./flightLayouts";
import FlightDetailsSplitLayout from "./FlightDetailsSplitLayout";
import SummaryFooter from "../../../../Components/InfoBox/SummaryFooter";
import { useSelectedResult } from "../../../../contexts/SelectedResultContext";
import { formatUsdDisplayInteger } from "../../../../utils/ceilUsd";
import { isAgentFlightResultsListUrl } from "../../flightBookingPathUtils";
import { shouldShowFlightBookingSidebar } from "./flightWizardSidebarVisibility";
import { resolveFlightEngineShellStep } from "./flightEngineShellStep";
import FlightEngineShell from "../../../../features/flightEngineV2/layout/FlightEngineShell";
import "../../../../features/flightEngineV2/design-system/tokens.css";
import "../v2/FlightResultsWorkspace.css";
import "./FlightWizard.css";

function FlightWizardSplitBody({
  panelKey,
  summary,
  primaryAfter,
  children,
}) {
  const { buildPhase } = useSelfComposeUi();
  return (
    <FlightDetailsSplitLayout
      panelKey={panelKey}
      summary={summary}
      primaryAfter={primaryAfter}
      primaryPanel={children}
      equalHeightCards={buildPhase === "summary"}
    />
  );
}

export default function FlightWizardShell({ children }) {
  const { pathname } = useLocation();
  const { flightStep: routeFlightStep } = useParams();
  const {
    loading,
    selectedSelfComposeOutboundDeal,
    selectedSelfComposeInboundDeal,
  } = useSearch();
  const {
    currentStep,
    pricing,
    flightReviewTermsAccepted,
    setFlightReviewTermsAccepted,
    selectedFlightOffer,
  } = useBookingFlow();
  const { ilsToUsd } = useSelectedResult();

  const onFlightResultsListUrl = isAgentFlightResultsListUrl(
    pathname,
    routeFlightStep
  );
  /** ב־URL תוצאות טיסות / בזמן טעינת חיפוש לא משתמשים בפריסת search (מסתירה את כל הילדים) */
  const searchPhase =
    currentStep === "search" && !onFlightResultsListUrl && !loading;
  const PageLayout = searchPhase ? FlightSearchPageLayout : FlightResultsPageLayout;

  /**
   * מפתח יציב ל־panel: כשה־URL הוא תוצאות אבל currentStep עדיין search, key="search"
   * גורם ל-remount מלא של הפאנל + אנימציית fw-panel--enter בכל סנכרון — נראה כמו בזיקה אינסופית.
   */
  const stableResultsPanelKey =
    (onFlightResultsListUrl || loading) && currentStep === "search"
      ? "results"
      : currentStep;

  const showReviewSummaryFooter = currentStep === "review";
  // Empty left rail while a self-compose direction is still missing.
  // Never apply this on the results list URL — it must not unmount the offer list.
  const hideEmptySelfComposeSidebar =
    currentStep === "selfComposePick" &&
    !onFlightResultsListUrl &&
    (!selectedSelfComposeOutboundDeal || !selectedSelfComposeInboundDeal);

  const engineShell = resolveFlightEngineShellStep({
    currentStep,
    onResultsListUrl: onFlightResultsListUrl,
    loading,
  });

  const footerPriceTextOverride = useMemo(() => {
    if (!showReviewSummaryFooter || !Number.isFinite(Number(pricing?.total))) {
      return null;
    }
    const ilsTotal = Math.ceil(Number(pricing.total) - 1e-9);
    const ilsRounded = ilsTotal.toLocaleString("he-IL");
    const usdVal =
      typeof ilsToUsd === "function" ? Number(ilsToUsd(ilsTotal)) : NaN;
    const usdStr = formatUsdDisplayInteger(Number.isFinite(usdVal) ? usdVal : 0);
    return `${ilsRounded}₪ (${usdStr}$)`;
  }, [showReviewSummaryFooter, pricing?.total, ilsToUsd]);

  // First-search loading uses the results layout (searchPhase is off) but
  // must not mount סיכום הזמנה until there is a real selected / priced offer.
  const showBookingSidebar = shouldShowFlightBookingSidebar({
    currentStep,
    onFlightResultsListUrl,
    loading,
    selectedFlightOffer,
    hideEmptySelfComposeSidebar,
    flightEngineUiV2DetailsSurface: engineShell?.step === "details",
  });

  const rootClass = [
    "fw-root",
    searchPhase ? "fw-root--phase-search" : "fw-root--phase-results",
    !engineShell && showReviewSummaryFooter ? "fw-root--review-summary-footer" : "",
    engineShell ? "fw-root--fe2-host fe2" : "",
    engineShell?.step === "details" ? "fw-root--fe-v2-details" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <div className={rootClass} dir="rtl" data-fw-step={currentStep}>
        {!searchPhase && !engineShell ? (
          <header className="fw-top">
            <FlightWizardStepStrip />
          </header>
        ) : null}

        <div className="fw-body">
          <main className="fw-main">
            <PageLayout>
              {engineShell ? (
                <FlightEngineShell
                  bare
                  step={engineShell.step}
                  title={engineShell.title}
                  previewNav={null}
                >
                  {children}
                </FlightEngineShell>
              ) : searchPhase ? (
                <div className="fw-search-phase-slot" aria-hidden="true">
                  {children}
                </div>
              ) : (
                <SelfComposeUiProvider enabled={currentStep === "selfComposePick"}>
                  <>
                    <FlightWizardStepHeader />
                    <FlightWizardSplitBody
                      panelKey={stableResultsPanelKey}
                      summary={
                        showBookingSidebar ? <FlightBookingSidebar /> : null
                      }
                      primaryAfter={<FlightWizardNavigation />}
                    >
                      {children}
                    </FlightWizardSplitBody>
                  </>
                </SelfComposeUiProvider>
              )}
            </PageLayout>
          </main>
        </div>
      </div>
      {!engineShell && showReviewSummaryFooter ? (
        <SummaryFooter
          beforePayment={false}
          nextStageDisabled={false}
          isConditionsConfirmed={flightReviewTermsAccepted}
          setIsConditionsConfirmed={setFlightReviewTermsAccepted}
          showTermsCheckbox
          termsCheckboxBeforeContinue
          showContinueButton={false}
          footerPriceTextOverride={footerPriceTextOverride}
        />
      ) : null}
    </>
  );
}
